// ============================================================================
// Admin → Rate changes (/admin/rate-proposals)
//
// A rate change is MADE at
// src/modules/account-management/components/rate-change-modal.tsx, and it takes
// effect on the date it is given. This page is the RECORD: what is booked for a
// night that has not arrived, what is left over from the approve-first process
// that ran until 2026-09-08, and the history.
//
// ⛔ APPROVING NO LONGER CHANGES A RATE. Ben, 2026-09-08: the change takes
// effect and Finance, the National AM and the executives are told. In thirteen
// months the approval step had never once said no — 130 approved, 0 rejected,
// 23 of them approved by the person who proposed them.
//
// The approve / reject buttons survive for ONE reason: the rows that were
// already waiting when that changed, so nobody's pending change is stranded.
// public.rcp_apply_on_approve() refuses to apply anything created after the
// changeover and says where to go instead. When this list empties, that trigger
// and these buttons can go.
//
// Access: finance, executive, superuser. The UI guard short-circuits with
// a friendly redirect; RLS independently enforces visibility (am_read_by_
// facility / finance_exec_read_all) + the decider_act UPDATE policy.
// ============================================================================

import { seesRateHistory } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import { errorMessage } from '../../shared/errors';

// Who may open this page: seesRateHistory (shared/role-gates) — finance,
// executives and superusers from the Money menu, and sales and account
// managers from a building card's rate history link (Ben, 2026-10-21). The
// page is read-only.

// Fields that should render as currency in the diff display. Anything else
// is shown as a plain numeric.
const CURRENCY_FIELDS = new Set([
  'monthly_rate',
  'hourly_rate',
  'sub_rate_monthly',
  'sub_rate_hourly',
  // The hourly sub rate is a rate-change field (TCSD-0492, items 831/758).
  'sub_hourly_rate',
  'billing_rate',
]);

type ProposalStatus =
  | 'Applied'
  | 'Scheduled'
  | 'Cancelled'
  // History. 134 rows carry these from the approve-first process that ran until
  // 2026-09-08. They are never rewritten.
  | 'Pending'
  | 'Approved'
  | 'Rejected'
  | 'Withdrawn';

interface ProposalRow {
  proposal_id: string;
  facility_id: string;
  target_table: string;
  target_row_id: string | null;
  field_name: string;
  current_value: number | null;
  proposed_value: number;
  reason: string;
  proposed_by_eid: string;
  proposed_at: string;
  status: ProposalStatus;
  decided_by_eid: string | null;
  decided_at: string | null;
  decision_notes: string | null;
  applied_at: string | null;
  /** The annual increase this change belongs to (item #706), or null. */
  kv_id: string | null;
  effective_date: string | null;
  /** The anniversary the linked annual increase was validated for. */
  increase_date: string | null;
  facility_name: string | null;
  proposer_name: string | null;
}

interface ProposalRowFromDb extends Omit<ProposalRow, 'facility_name' | 'proposer_name' | 'increase_date'> {
  facilities: { facility_name: string | null } | null;
  increase: { kicker_date: string | null } | null;
  proposer: {
    employee_name_first: string | null;
    employee_name_last: string | null;
  } | null;
}

const SELECT = `
  proposal_id, facility_id, target_table, target_row_id,
  field_name, current_value, proposed_value, reason,
  proposed_by_eid, proposed_at, status,
  decided_by_eid, decided_at, decision_notes, applied_at, kv_id, effective_date,
  facilities ( facility_name ),
  increase:kicker_validations!rate_change_proposals_kv_id_fkey ( kicker_date ),
  proposer:employees!rate_change_proposals_proposed_by_eid_fkey (
    employee_name_first, employee_name_last
  )
`;

function flatten(rows: ProposalRowFromDb[]): ProposalRow[] {
  return rows.map((r) => ({
    proposal_id: r.proposal_id,
    facility_id: r.facility_id,
    target_table: r.target_table,
    target_row_id: r.target_row_id,
    field_name: r.field_name,
    current_value: r.current_value,
    proposed_value: r.proposed_value,
    reason: r.reason,
    proposed_by_eid: r.proposed_by_eid,
    proposed_at: r.proposed_at,
    status: r.status,
    decided_by_eid: r.decided_by_eid,
    decided_at: r.decided_at,
    decision_notes: r.decision_notes,
    applied_at: r.applied_at,
    kv_id: r.kv_id ?? null,
    effective_date: r.effective_date ?? null,
    increase_date: r.increase?.kicker_date ?? null,
    facility_name: r.facilities?.facility_name ?? null,
    proposer_name:
      [r.proposer?.employee_name_first, r.proposer?.employee_name_last]
        .filter(Boolean)
        .join(' ')
        .trim() || null,
  }));
}

function fmtValue(field: string, value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (CURRENCY_FIELDS.has(field)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(value);
  }
  return value.toLocaleString('en-US');
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diffMs = Date.now() - t;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function statusPillClass(s: ProposalStatus): string {
  switch (s) {
    case 'Pending':
      return 'bg-st-warn-bg text-st-warn-fg-strong ring-st-warn-border';
    case 'Approved':
      return 'bg-st-ok-bg text-st-ok-fg-strong ring-st-ok-border';
    case 'Rejected':
      return 'bg-st-danger-bg text-st-danger-fg-strong ring-st-danger-border';
    case 'Withdrawn':
    case 'Cancelled':
      return 'bg-tcs-bg-muted text-tcs-text-secondary ring-tcs-border';
    case 'Applied':
      return 'bg-st-ok-bg text-st-ok-fg-strong ring-st-ok-border';
    case 'Scheduled':
      return 'bg-st-info-bg text-st-info-fg-strong ring-st-info-border';
  }
}

export default function AdminRateProposals() {
  const { profile } = useAuth();
  const role = profile?.role;
  const allowed = seesRateHistory(role);

  const [rows, setRows] = useState<ProposalRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const [banner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('rate_change_proposals')
          .select(SELECT)
          .order('proposed_at', { ascending: false });
        if (cancelled) return;
        if (e) throw e;
        setRows(flatten((data ?? []) as unknown as ProposalRowFromDb[]));
      } catch (err) {
        if (cancelled) return;
        console.error('[rate-proposals] load failed', err);
        setError(errorMessage(err, 'Failed to load proposals.', 'rate-proposals'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  // ?facility=FAC-… narrows the page to one building's whole rate history (the
  // account card links here; item #706). Without it, the 25 most recent.
  const [params] = useSearchParams();
  const facilityFilter = params.get('facility');
  const { scheduled, decided } = useMemo(() => {
    const all = (rows ?? []).filter((r) => !facilityFilter || r.facility_id === facilityFilter);
    return {
      // Booked for a night that has not arrived. Anyone may call one off.
      scheduled: all.filter((r) => r.status === 'Scheduled'),
      // Everything else is history, the 3 pre-changeover Pending rows included
      // — they were decided on 2026-09-08 and no new one can exist.
      decided: all.filter((r) => r.status !== 'Scheduled').slice(0, facilityFilter ? undefined : 25),
    };
  }, [rows, facilityFilter]);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl p-4 lg:p-6">
        <PageHeader
          title="Rate changes"
          meta="Every rate change, and what is booked for later."
        />
        <div className="mt-4 rounded-md border border-tcs-border bg-tcs-raised p-6 text-center text-sm text-tcs-text-secondary">
          You don't have access to this page. If you need to see rate changes,
          ask the COO to grant the <code>finance</code> or{' '}
          <code>executive</code> role.
        </div>
      </div>
    );
  }


  return (
    <div className="mx-auto max-w-5xl p-4 lg:p-6">
      <PageHeader
        title="Rate changes"
        meta="A rate change takes effect when it is recorded. Finance, the National Account Manager and the executives are told."
        actions={
          <Button variant="ghost" onClick={refetch} aria-label="Reload">
            <RefreshCw size={14} className="mr-1" />
            Reload
          </Button>
        }
      />

      {banner && (
        <div
          className={`mt-4 rounded-md px-3 py-2 text-sm ${
            banner.kind === 'success'
              ? 'bg-st-ok-bg text-st-ok-fg-strong'
              : 'bg-st-danger-bg text-st-danger-fg-strong'
          }`}
          role="status"
        >
          {banner.text}
        </div>
      )}

      <section className="mt-4">
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
          Booked for later ({scheduled.length})
        </h2>
        {error && (
          <ErrorState title="Could not load rate changes" body={error} onRetry={refetch} />
        )}
        {loading && !error && (
          <div className="space-y-2">
            <Skeleton block height={72} />
            <Skeleton block height={72} />
          </div>
        )}
        {!loading && !error && scheduled.length === 0 && (
          <EmptyState
            title="Nothing booked"
            body="A rate change takes effect the moment it is recorded. One given a future date waits here until that night, and anyone can call it off before then."
          />
        )}
        {!loading && !error && scheduled.length > 0 && (
          <ul className="space-y-2">
            {scheduled.map((p) => (
              <ProposalCard key={p.proposal_id} row={p} />
            ))}
          </ul>
        )}
      </section>

      {!loading && !error && decided.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            {facilityFilter ? `Rate history — ${decided[0]?.facility_name ?? facilityFilter}` : 'Recently decided'}
          </h2>
          {facilityFilter && (
            <p className="mb-2 text-xs text-tcs-text-secondary">
              Every rate change on record for this building.{' '}
              <Link to="/money/rate-proposals" className="underline">
                Show all buildings
              </Link>
            </p>
          )}
          <ul className="space-y-2">
            {decided.map((p) => (
              <ProposalCard key={p.proposal_id} row={p} />
            ))}
          </ul>
        </section>
      )}

    </div>
  );
}

interface ProposalCardProps {
  row: ProposalRow;
}

// ⛔ This card used to carry Approve and Reject buttons. They are gone with the
// process they belonged to: a rate change takes effect on the date it is given
// (TCSD-0197), so there is nothing here to decide. The card is a record.
function ProposalCard({ row }: ProposalCardProps) {
  return (
    <li className="rounded-md border border-tcs-border bg-tcs-raised p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs text-tcs-text-muted">
            <span className="font-mono text-[11px] text-tcs-text-muted">
              {row.proposal_id}
            </span>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${statusPillClass(row.status)}`}
            >
              {row.status}
            </span>
            {row.kv_id && (
              <span
                className="inline-flex items-center rounded bg-tcs-bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-tcs-text"
                title={`Linked to annual increase ${row.kv_id}`}
              >
                Annual increase
              </span>
            )}
            <span className="text-tcs-text-muted">·</span>
            <span>{fmtRelative(row.proposed_at)}</span>
            {row.effective_date ? (
              <>
                <span className="text-tcs-text-muted">·</span>
                <span>takes effect {new Date(`${row.effective_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </>
            ) : row.kv_id ? (
              <>
                <span className="text-tcs-text-muted">·</span>
                <span>
                  start date not kept on this older record
                  {row.increase_date
                    ? ` · increase validated for ${new Date(`${row.increase_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : ''}
                </span>
              </>
            ) : null}
            {row.applied_at && (
              <>
                <span className="text-tcs-text-muted">·</span>
                <span className="text-st-ok-fg">
                  applied {fmtRelative(row.applied_at)}
                </span>
              </>
            )}
          </div>

          <div className="mt-1 text-sm text-tcs-text">
            <Link
              to={`/account-management/facilities/${row.facility_id}`}
              className="font-medium hover:underline"
            >
              {row.facility_name ?? row.facility_id}
            </Link>
            <span className="text-tcs-text-muted"> · </span>
            <span className="text-tcs-text-secondary">
              {row.target_table}.{row.field_name}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap items-baseline gap-2 text-sm">
            <span className="text-tcs-text-muted">
              {fmtValue(row.field_name, row.current_value)}
            </span>
            <span className="text-tcs-text-muted">→</span>
            <span className="font-medium tabular-nums text-tcs-text">
              {fmtValue(row.field_name, row.proposed_value)}
            </span>
          </div>

          <p className="mt-2 text-sm text-tcs-text-secondary">
            <span className="text-xs uppercase tracking-wide text-tcs-text-muted">
              Reason ·{' '}
            </span>
            {row.reason}
          </p>

          <p className="mt-2 text-xs text-tcs-text-muted">
            Proposed by{' '}
            <span className="text-tcs-text-secondary">
              {row.proposer_name ?? row.proposed_by_eid}
            </span>
          </p>

          {row.decision_notes && (
            <p className="mt-2 rounded bg-tcs-bg-muted px-2 py-1.5 text-xs text-tcs-text-secondary">
              <span className="font-medium text-tcs-text-secondary">Decision notes:</span>{' '}
              {row.decision_notes}
            </p>
          )}
        </div>

      </div>
    </li>
  );
}
